import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "motion/react";
import {
  Plus,
  LayoutGrid,
  List,
  Sparkles,
  CircleDashed,
  Circle,
  CircleDot,
  Eye,
  CheckCircle2,
  Bot,
  AlertTriangle,
  Trash2,
  X,
  Zap,
  GitBranch,
  Boxes,
  Pencil,
  Archive,
  Check,
} from "lucide-react";
import { getTemplateMeta } from "@/lib/templateMeta";
import { useCompanyId } from "@/lib/CompanyContext";
import { useIsMobile } from "@/lib/useIsMobile";
import {
  issuesApi,
  type Issue,
  type IssueStatus,
  type IssueLabel,
  type IssuePriority,
} from "@/api/issues";
import { routinesApi } from "@/api/routines";
import { ndAgentsApi } from "@/api/nd-agents";
import { projectsApi, ESPACO_COLORS, type Espaco } from "@/api/projects";
import { queryKeys } from "@/lib/queryKeys";

type ViewMode = "kanban" | "list";

// ─────────────────────────────────────────────────────────────
// Status / coluna meta
// ─────────────────────────────────────────────────────────────

interface StatusMeta {
  label: string;
  primary: IssueStatus;
  matches: IssueStatus[];
  icon: typeof Circle;
  color: string;
  bgTint: string;
  /** Quando setado, este coluna captura issues com este originKind ANTES
   *  de qualquer match por status. Usado pra "Automação" (todas as tasks
   *  geradas por routines, independentemente do status). */
  originKind?: string;
}

const STATUS_COLUMNS: StatusMeta[] = [
  {
    label: "Automação",
    primary: "todo",
    matches: [],
    originKind: "routine_execution",
    icon: Zap,
    color: "var(--nd-blue)",
    bgTint: "rgba(6,62,110,0.08)",
  },
  {
    label: "Em criação",
    primary: "todo",
    matches: ["backlog", "todo"],
    icon: CircleDashed,
    color: "var(--wf-ink-3)",
    bgTint: "rgba(128,128,128,0.06)",
  },
  {
    label: "Executando",
    primary: "in_progress",
    matches: ["in_progress"],
    icon: CircleDot,
    color: "var(--nd-orange)",
    bgTint: "rgba(255,112,0,0.08)",
  },
  {
    label: "Bloqueado",
    primary: "blocked",
    matches: ["blocked"],
    icon: AlertTriangle,
    color: "var(--nd-orange-pun)",
    bgTint: "rgba(204,51,68,0.08)",
  },
  {
    label: "Revisar",
    primary: "in_review",
    matches: ["in_review"],
    icon: Eye,
    color: "var(--nd-blue)",
    bgTint: "rgba(6,62,110,0.08)",
  },
  {
    label: "Concluído",
    primary: "done",
    matches: ["done"],
    icon: CheckCircle2,
    color: "#5cb85c",
    bgTint: "rgba(92,184,92,0.06)",
  },
];

function getStatusMeta(status: IssueStatus): StatusMeta {
  // Manual issues — primeira coluna sem originKind que case com o status.
  return (
    STATUS_COLUMNS.find((c) => !c.originKind && c.matches.includes(status)) ??
    STATUS_COLUMNS.find((c) => !c.originKind) ??
    STATUS_COLUMNS[0]!
  );
}

/** Decide em qual coluna a issue cai. originKind tem precedência sobre status. */
function columnForIssue(issue: Issue): StatusMeta {
  // 1) Coluna por originKind
  const byOrigin = STATUS_COLUMNS.find(
    (c) => c.originKind && c.originKind === issue.originKind,
  );
  if (byOrigin) return byOrigin;
  // 2) Caso normal por status
  return getStatusMeta(issue.status);
}

// ─────────────────────────────────────────────────────────────
// Priority — só mostra quando crítico (resto não polui o card)
// ─────────────────────────────────────────────────────────────

function getPriorityIndicator(priority: IssuePriority) {
  if (priority === "critical") {
    return { icon: AlertTriangle, color: "#cc3344", label: "crítica" };
  }
  return null;
}

// ─────────────────────────────────────────────────────────────
// Time helper
// ─────────────────────────────────────────────────────────────

function formatRelative(iso: string): string {
  const d = new Date(iso).getTime();
  const diffMs = Date.now() - d;
  if (diffMs < 60_000) return "agora";
  if (diffMs < 3_600_000) return `há ${Math.round(diffMs / 60_000)}min`;
  if (diffMs < 86_400_000) return `há ${Math.round(diffMs / 3_600_000)}h`;
  if (diffMs < 7 * 86_400_000) return `há ${Math.round(diffMs / 86_400_000)}d`;
  return new Intl.DateTimeFormat("pt-BR", { day: "numeric", month: "short" }).format(new Date(iso));
}

// ─────────────────────────────────────────────────────────────
// Card
// ─────────────────────────────────────────────────────────────

function TaskCard({
  issue,
  labels,
  agentNames,
  espacoById,
  showEspaco,
  onDragStart,
  onDragEnd,
  onDelete,
  delay = 0,
}: {
  issue: Issue;
  labels: IssueLabel[];
  /** id do agente → nome (pro chip do responsável mostrar quem é). */
  agentNames?: Map<string, string>;
  /** id do espaço → espaço (pra bolinha de cor na visão "Todos"). */
  espacoById?: Map<string, Espaco>;
  showEspaco?: boolean;
  onDragStart: (e: React.DragEvent, issueId: string) => void;
  onDragEnd?: () => void;
  onDelete?: (issueId: string) => void;
  delay?: number;
}) {
  const issueLabels = (issue.labelIds ?? [])
    .map((id) => labels.find((l) => l.id === id))
    .filter((l): l is IssueLabel => !!l);
  const cardEspaco =
    showEspaco && issue.projectId ? espacoById?.get(issue.projectId) : undefined;
  const agentName = issue.assigneeAgentId
    ? agentNames?.get(issue.assigneeAgentId)
    : undefined;
  const isActive = issue.status === "in_progress";
  // in_review e done abrem o entregável; os outros vão pra tela de execução
  const isDone = issue.status === "done" || issue.status === "in_review";
  const templateMeta = getTemplateMeta(issue.templateKey);
  const TemplateIcon = templateMeta?.icon;
  const priorityMeta = getPriorityIndicator(issue.priority);
  const PriorityIcon = priorityMeta?.icon;
  const [hover, setHover] = useState(false);
  // Barra de status colorida na lateral — leitura do estado num relance
  // (feedback do beta: "status com cores"). Verde=pronto, âmbar=em andamento/
  // revisão, vermelho=travado, cinza=a fazer.
  const STATUS_ACCENT: Record<string, string> = {
    todo: "var(--wf-ink-4)",
    in_progress: "var(--nd-orange)",
    in_review: "#e08a2b",
    done: "#3aa657",
    blocked: "#d92d4a",
  };
  const statusAccent = STATUS_ACCENT[issue.status] ?? "var(--wf-ink-4)";

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay, ease: [0.16, 1, 0.3, 1] }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ position: "relative" }}
    >
      {onDelete && hover && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onDelete(issue.id);
          }}
          aria-label="Deletar tarefa"
          title="Deletar tarefa"
          style={{
            position: "absolute",
            top: -6,
            right: -6,
            width: 22,
            height: 22,
            borderRadius: 999,
            background: "var(--wf-paper)",
            border: "1px solid var(--wf-ink-5)",
            color: "var(--wf-ink-3)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            zIndex: 2,
            boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
          }}
        >
          <X size={12} strokeWidth={2.25} />
        </button>
      )}
      <Link
        to={isDone ? `/resultado/${issue.id}` : `/execucao/${issue.id}`}
        draggable
        onDragStart={(e) => onDragStart(e, issue.id)}
        onDragEnd={onDragEnd}
        className={isActive ? "wf-neon-border" : undefined}
        style={{
          display: "block",
          textDecoration: "none",
          color: "inherit",
          cursor: "grab",
          padding: 12,
          background: "var(--wf-paper)",
          border: isActive
            ? "1px solid rgba(255,112,0,0.25)"
            : hover
              ? "1px solid var(--nd-orange)"
              : "1px solid var(--wf-ink-5)",
          borderLeft: `3px solid ${statusAccent}`,
          borderRadius: 10,
          boxShadow: isActive
            ? "0 0 16px rgba(255,112,0,0.10)"
            : "0 1px 0 rgba(0,0,0,0.03)",
          transition: "border-color 150ms",
        }}
      >
        {/* Top row — template type + priority */}
        {(templateMeta || priorityMeta || issue.identifier || issue.parentId) && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              marginBottom: 8,
              fontSize: 11,
            }}
          >
            {issue.parentId && (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  color: "var(--nd-blue)",
                  flexShrink: 0,
                }}
                title="Sub-tarefa de outra tarefa"
              >
                <GitBranch size={11} strokeWidth={2} />
              </span>
            )}
            {templateMeta && TemplateIcon && (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  padding: "2px 7px",
                  background: "rgba(255,112,0,0.10)",
                  color: "var(--nd-orange-pun)",
                  borderRadius: 999,
                  fontWeight: 600,
                  fontSize: 10,
                }}
              >
                <TemplateIcon size={10} strokeWidth={2} />
                {templateMeta.label}
              </span>
            )}
            {priorityMeta && PriorityIcon && (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 3,
                  padding: "1px 6px",
                  background: `${priorityMeta.color}18`,
                  color: priorityMeta.color,
                  fontWeight: 600,
                  fontSize: 10,
                  borderRadius: 999,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
                title="prioridade crítica"
              >
                <PriorityIcon size={10} strokeWidth={2.5} />
                {priorityMeta.label}
              </span>
            )}
            <span style={{ marginLeft: "auto", fontSize: 10, color: "var(--wf-ink-3)", fontFamily: "var(--wf-font-body)" }}>
              {issue.identifier ?? ""}
            </span>
          </div>
        )}

        {/* Title */}
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: "-0.005em",
            lineHeight: 1.3,
            color: "var(--wf-ink)",
            marginBottom: 8,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {issue.title}
        </div>

        {/* Footer — agent + label + relative time */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: 11,
            color: "var(--wf-ink-3)",
          }}
        >
          {issue.assigneeAgentId && (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                padding: "1px 7px",
                background: "var(--wf-paper)",
                border: "1px solid var(--wf-ink-5)",
                borderRadius: 999,
                color: "var(--wf-ink-2)",
                fontWeight: 500,
                maxWidth: 130,
                overflow: "hidden",
              }}
              title={agentName ? `Responsável: ${agentName}` : "Agente responsável"}
            >
              {isActive && (
                <span
                  style={{
                    width: 5,
                    height: 5,
                    borderRadius: "50%",
                    background: "var(--nd-orange)",
                    animation: "wf-pulse 1.6s infinite",
                    flexShrink: 0,
                  }}
                />
              )}
              <Bot size={10} strokeWidth={1.75} style={{ flexShrink: 0 }} />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {agentName ?? "agente"}
              </span>
            </span>
          )}
          {issueLabels.slice(0, 2).map((l) => (
            <span
              key={l.id}
              style={{
                fontSize: 10,
                fontWeight: 600,
                padding: "1px 7px",
                borderRadius: 999,
                background: l.color ? `${l.color}1c` : "var(--wf-bg)",
                color: l.color || "var(--wf-ink-2)",
                maxWidth: 90,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {l.name}
            </span>
          ))}
          <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 6 }}>
            {cardEspaco && (
              <span
                title={`Espaço: ${cardEspaco.name}`}
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: 999,
                  background: cardEspaco.color || "var(--wf-ink-4)",
                  flexShrink: 0,
                }}
              />
            )}
            <span style={{ fontSize: 10 }}>{formatRelative(issue.updatedAt)}</span>
          </span>
        </div>
      </Link>
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────
// Kanban column
// ─────────────────────────────────────────────────────────────

function KanbanColumn({
  meta,
  issues,
  labels,
  agentNames,
  espacoById,
  showEspaco,
  onDrop,
  onDragStart,
  onDragEnd,
  onDeleteIssue,
  automationCounts,
}: {
  meta: StatusMeta;
  issues: Issue[];
  labels: IssueLabel[];
  agentNames?: Map<string, string>;
  espacoById?: Map<string, Espaco>;
  showEspaco?: boolean;
  onDrop: (status: IssueStatus) => void;
  onDragStart: (e: React.DragEvent, issueId: string) => void;
  onDragEnd: () => void;
  onDeleteIssue: (issueId: string) => void;
  /** Quando setado na coluna Automação, mostra footer informando rituais
   *  que rodaram nos últimos 7 dias sem criar tarefa. Separa entre os
   *  que fizeram trabalho real (worked, ex: agentes atualizados) e os
   *  que de fato pularam por falta de entregas (silent). */
  automationCounts?: { worked: number; silent: number };
}) {
  const [over, setOver] = useState(false);
  const Icon = meta.icon;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 10,
        minHeight: 0,
        flex: "0 0 280px",
        minWidth: 280,
      }}
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={() => {
        setOver(false);
        onDrop(meta.primary);
      }}
    >
      {/* Column header — leve, estilo ClickUp: pill suave na cor do status +
          contagem solta em cinza (sem caixa pesada de borda inteira). */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "2px 4px",
        }}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "4px 11px",
            borderRadius: 999,
            background: meta.bgTint,
            color: meta.color,
            fontSize: 11.5,
            fontWeight: 700,
            letterSpacing: "0.01em",
          }}
        >
          <Icon size={12} strokeWidth={2} />
          {meta.label}
        </span>
        <span style={{ fontSize: 11.5, fontWeight: 600, color: "var(--wf-ink-3)" }}>
          {issues.length}
        </span>
      </div>

      {/* Drop zone */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          minHeight: 80,
          padding: 4,
          borderRadius: 10,
          background: over ? "rgba(255,112,0,0.06)" : "transparent",
          border: over ? "1px dashed var(--nd-orange)" : "1px dashed transparent",
          flex: 1,
          overflow: "auto",
          transition: "background 120ms",
        }}
      >
        <AnimatePresence mode="popLayout">
          {issues.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              style={{
                fontSize: 11,
                color: "var(--wf-ink-3)",
                padding: "16px 8px",
                textAlign: "center",
                fontStyle: "italic",
              }}
            >
              sem tarefas
            </motion.div>
          )}
          {issues.map((issue, i) => (
            <TaskCard
              key={issue.id}
              issue={issue}
              labels={labels}
              agentNames={agentNames}
              espacoById={espacoById}
              showEspaco={showEspaco}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              onDelete={onDeleteIssue}
              delay={i * 0.03}
            />
          ))}
        </AnimatePresence>

        {/* Footer informativo só na coluna Automação. Separa rituais que
            fizeram trabalho real (atualizaram padrões, etc) dos que de fato
            pularam por falta de entregas. Click leva pra /automacoes. */}
        {meta.originKind === "routine_execution" &&
          automationCounts &&
          (automationCounts.worked > 0 || automationCounts.silent > 0) && (
            <Link
              to="/automacoes"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 4,
                marginTop: issues.length > 0 ? 6 : 0,
                padding: "10px 12px",
                fontSize: 11,
                color: "var(--wf-ink-3)",
                background: "var(--wf-bg)",
                border: "1px dashed var(--wf-ink-5)",
                borderRadius: 8,
                textDecoration: "none",
                lineHeight: 1.4,
              }}
              title="Rituais que rodaram nos últimos 7 dias sem criar tarefa nova"
            >
              {automationCounts.worked > 0 && (
                <span>
                  ✓ {automationCounts.worked}{" "}
                  {automationCounts.worked === 1
                    ? "ritual atualizou padrões"
                    : "rituais atualizaram padrões"}{" "}
                  esta semana
                </span>
              )}
              {automationCounts.silent > 0 && (
                <span style={{ fontStyle: "italic" }}>
                  + {automationCounts.silent}{" "}
                  {automationCounts.silent === 1 ? "rodou" : "rodaram"} sem
                  entregas pra processar
                </span>
              )}
              <span
                style={{
                  fontSize: 10,
                  color: "var(--nd-orange)",
                  fontWeight: 600,
                  marginTop: 2,
                }}
              >
                ver detalhes →
              </span>
            </Link>
          )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// List view — agrupada por status com sticky headers
// ─────────────────────────────────────────────────────────────

function ListView({
  issues,
  labels,
}: {
  issues: Issue[];
  labels: IssueLabel[];
}) {
  const grouped = STATUS_COLUMNS.map((col) => ({
    meta: col,
    items: issues.filter((i) => columnForIssue(i) === col),
  }));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {grouped.map(({ meta, items }) => {
        if (items.length === 0) return null;
        const Icon = meta.icon;
        return (
          <section key={meta.primary}>
            {/* Section header */}
            <header
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "8px 14px",
                background: meta.bgTint,
                borderRadius: 8,
                border: "1px solid var(--wf-ink-5)",
                marginBottom: 8,
                position: "sticky",
                top: 0,
                zIndex: 1,
                backdropFilter: "blur(8px)",
              }}
            >
              <Icon size={14} strokeWidth={1.75} color={meta.color} />
              <h2
                className="wf-display"
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  color: "var(--wf-ink)",
                  margin: 0,
                }}
              >
                {meta.label}
              </h2>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: meta.color,
                  padding: "1px 8px",
                  background: "var(--wf-paper)",
                  borderRadius: 999,
                }}
              >
                {items.length}
              </span>
              <div style={{ flex: 1, borderTop: "1px dashed var(--wf-ink-5)" }} />
            </header>

            {/* Items */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 0,
                background: "var(--wf-paper)",
                border: "1px solid var(--wf-ink-5)",
                borderRadius: 10,
                overflow: "hidden",
              }}
            >
              {items.map((issue, i) => {
                const issueLabels = (issue.labelIds ?? [])
                  .map((id) => labels.find((l) => l.id === id))
                  .filter((l): l is IssueLabel => !!l);
                const primaryLabel = issueLabels[0];
                const templateMeta = getTemplateMeta(issue.templateKey);
                const TemplateIcon = templateMeta?.icon;
                const priorityMeta = getPriorityIndicator(issue.priority);
                const PriorityIcon = priorityMeta?.icon;

                return (
                  <Link
                    key={issue.id}
                    to={
                      issue.status === "done" || issue.status === "in_review"
                        ? `/resultado/${issue.id}`
                        : `/execucao/${issue.id}`
                    }
                    style={{
                      display: "grid",
                      gridTemplateColumns: "auto 1fr auto auto auto",
                      gap: 12,
                      padding: "11px 16px",
                      borderTop: i === 0 ? "none" : "1px solid var(--wf-ink-5)",
                      alignItems: "center",
                      textDecoration: "none",
                      color: "inherit",
                      transition: "background 100ms",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,112,0,0.04)")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                  >
                    {/* Priority indicator */}
                    {priorityMeta && PriorityIcon ? (
                      <PriorityIcon size={12} strokeWidth={2} color={priorityMeta.color} />
                    ) : (
                      <div style={{ width: 12 }} />
                    )}

                    {/* Title + identifier */}
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          marginBottom: 1,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: "var(--wf-ink)",
                            letterSpacing: "-0.005em",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {issue.title}
                        </span>
                        {issue.status === "done" &&
                          issue.executionState?.rejectedAndClosed && (
                            <span
                              style={{
                                flexShrink: 0,
                                fontSize: 10,
                                fontWeight: 700,
                                color: "#d92d4a",
                                background: "rgba(217,45,74,0.10)",
                                padding: "1px 7px",
                                borderRadius: 999,
                                textTransform: "uppercase",
                                letterSpacing: "0.02em",
                              }}
                            >
                              Reprovado
                            </span>
                          )}
                      </div>
                      {issue.identifier && (
                        <div
                          style={{
                            fontSize: 11,
                            color: "var(--wf-ink-3)",
                            fontFamily: "var(--wf-font-body)",
                          }}
                        >
                          {issue.identifier} · atualizada {formatRelative(issue.updatedAt)}
                        </div>
                      )}
                    </div>

                    {/* Template type */}
                    <div>
                      {templateMeta && TemplateIcon && (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 5,
                            padding: "3px 9px",
                            background: "rgba(255,112,0,0.10)",
                            color: "var(--nd-orange-pun)",
                            borderRadius: 999,
                            fontSize: 11,
                            fontWeight: 600,
                          }}
                        >
                          <TemplateIcon size={11} strokeWidth={2} />
                          {templateMeta.label}
                        </span>
                      )}
                    </div>

                    {/* Label */}
                    <div>
                      {primaryLabel && (
                        <span
                          className="wf-pill wf-pill--gray"
                          style={{ fontSize: 11, padding: "1px 8px" }}
                        >
                          {primaryLabel.name}
                        </span>
                      )}
                    </div>

                    {/* Agent + arrow */}
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {issue.assigneeAgentId && (
                        <Bot size={12} strokeWidth={1.75} color="var(--wf-ink-3)" />
                      )}
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: "var(--nd-orange-pun)",
                        }}
                      >
                        abrir
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Espaços — organização do kanban (campanhas/produtos/frentes).
// É SÓ um agrupador de tarefas: a empresa continua sendo a unidade
// (onboarding/ESCALA/agentes não mudam por espaço).
// ─────────────────────────────────────────────────────────────

/** Filtro de espaço: null = Todos · "geral" = sem espaço · senão o id. */
type EspacoFilter = string | null;

function EspacoDot({ color, size = 8 }: { color?: string | null; size?: number }) {
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: 999,
        background: color || "var(--wf-ink-4)",
        flexShrink: 0,
      }}
    />
  );
}

function EspacosPanel({
  espacos,
  selected,
  onSelect,
  countAll,
  countGeral,
  countFor,
  onCreate,
  onUpdate,
  onArchive,
  onDropIssue,
  dragging,
  busy,
}: {
  espacos: Espaco[];
  selected: EspacoFilter;
  onSelect: (v: EspacoFilter) => void;
  countAll: number;
  countGeral: number;
  countFor: (id: string) => number;
  onCreate: (name: string, color: string) => void;
  onUpdate: (id: string, patch: { name?: string; color?: string }) => void;
  onArchive: (id: string) => void;
  /** Solta uma tarefa arrastada neste alvo (id do espaço, ou "geral"). */
  onDropIssue: (target: string) => void;
  /** Há um card sendo arrastado agora? (acende os alvos de soltura). */
  dragging?: boolean;
  busy?: boolean;
}) {
  const [creating, setCreating] = useState(false);
  const [draftName, setDraftName] = useState("");
  const [draftColor, setDraftColor] = useState<string>(ESPACO_COLORS[0]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  // Alvo de soltura sob o cursor durante o drag (id do espaço ou "geral").
  const [dropTarget, setDropTarget] = useState<string | null>(null);

  const itemStyle = (active: boolean, isDropOver = false): React.CSSProperties => ({
    display: "flex",
    alignItems: "center",
    gap: 8,
    width: "100%",
    padding: "7px 10px",
    borderRadius: 8,
    border: isDropOver ? "1px dashed var(--nd-orange)" : "1px solid transparent",
    background: isDropOver
      ? "rgba(255,112,0,0.12)"
      : active
        ? "var(--wf-paper)"
        : "transparent",
    boxShadow: active && !isDropOver ? "0 1px 2px rgba(0,0,0,0.05)" : "none",
    color: active ? "var(--wf-ink)" : "var(--wf-ink-2)",
    fontSize: 12.5,
    fontWeight: active ? 600 : 500,
    fontFamily: "var(--wf-font-body)",
    cursor: "pointer",
    textAlign: "left",
    transition: "background 120ms",
  });

  // Props de drop pra um alvo de soltura ("geral" ou id de espaço).
  const dropProps = (target: string) => ({
    onDragOver: (e: React.DragEvent) => {
      if (!dragging) return;
      e.preventDefault();
      setDropTarget(target);
    },
    onDragLeave: () => setDropTarget((t) => (t === target ? null : t)),
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      setDropTarget(null);
      onDropIssue(target);
    },
  });

  const colorPicker = (value: string, onPick: (c: string) => void) => (
    <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 6 }}>
      {ESPACO_COLORS.map((c) => (
        <button
          key={c}
          type="button"
          onClick={() => onPick(c)}
          aria-label={`cor ${c}`}
          style={{
            width: 16,
            height: 16,
            borderRadius: 999,
            background: c,
            border: value === c ? "2px solid var(--wf-ink)" : "2px solid transparent",
            cursor: "pointer",
            padding: 0,
          }}
        />
      ))}
    </div>
  );

  return (
    <div
      style={{
        width: 210,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        // Fundo um tom mais escuro que o rail (#efece4) — destoa de leve do
        // conteúdo, lendo como um painel próprio (estilo ClickUp).
        background: "#e8e3d8",
        border: "1px solid #ddd7ca",
        borderRadius: 12,
        padding: "10px 8px",
        alignSelf: "flex-start",
        maxHeight: "100%",
        overflowY: "auto",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "2px 8px 8px",
        }}
      >
        <span
          style={{
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: "0.07em",
            textTransform: "uppercase",
            color: "var(--wf-ink-3)",
          }}
        >
          Espaços
        </span>
        <button
          type="button"
          onClick={() => {
            setCreating((v) => !v);
            setDraftName("");
          }}
          title="Novo espaço"
          style={{
            width: 20,
            height: 20,
            borderRadius: 6,
            border: "1px solid var(--wf-ink-5)",
            background: "var(--wf-paper)",
            color: "var(--wf-ink-2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            padding: 0,
          }}
        >
          <Plus size={12} strokeWidth={2} />
        </button>
      </div>

      <button type="button" onClick={() => onSelect(null)} style={itemStyle(selected === null)}>
        <Boxes size={13} strokeWidth={1.75} style={{ flexShrink: 0, color: "var(--wf-ink-3)" }} />
        <span style={{ flex: 1 }}>Todos</span>
        <span style={{ fontSize: 11, color: "var(--wf-ink-3)" }}>{countAll}</span>
      </button>

      {espacos.map((e) => {
        const active = selected === e.id;
        const editing = editingId === e.id;
        return (
          <div key={e.id}>
            <div style={{ position: "relative" }} className="espaco-row">
              <button
                type="button"
                onClick={() => onSelect(e.id)}
                style={itemStyle(active, dropTarget === e.id)}
                {...dropProps(e.id)}
              >
                <EspacoDot color={e.color} />
                <span
                  style={{
                    flex: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {e.name}
                </span>
                <span style={{ fontSize: 11, color: "var(--wf-ink-3)" }}>{countFor(e.id)}</span>
              </button>
              <button
                type="button"
                title="Editar espaço"
                onClick={() => {
                  setEditingId(editing ? null : e.id);
                  setEditName(e.name);
                }}
                style={{
                  position: "absolute",
                  right: 28,
                  top: "50%",
                  transform: "translateY(-50%)",
                  width: 18,
                  height: 18,
                  borderRadius: 5,
                  border: "none",
                  background: "transparent",
                  color: "var(--wf-ink-3)",
                  display: active || editing ? "flex" : "none",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                <Pencil size={11} strokeWidth={1.75} />
              </button>
            </div>
            {editing && (
              <div
                style={{
                  margin: "4px 6px 8px",
                  padding: 10,
                  background: "var(--wf-paper)",
                  border: "1px solid var(--wf-ink-5)",
                  borderRadius: 8,
                }}
              >
                <input
                  value={editName}
                  onChange={(ev) => setEditName(ev.target.value)}
                  onKeyDown={(ev) => {
                    if (ev.key === "Enter" && editName.trim()) {
                      onUpdate(e.id, { name: editName.trim() });
                      setEditingId(null);
                    }
                    if (ev.key === "Escape") setEditingId(null);
                  }}
                  style={{
                    width: "100%",
                    border: "1px solid var(--wf-ink-5)",
                    borderRadius: 6,
                    padding: "5px 8px",
                    fontSize: 12,
                    fontFamily: "var(--wf-font-body)",
                    outline: "none",
                  }}
                />
                {colorPicker(e.color ?? "", (c) => onUpdate(e.id, { color: c }))}
                <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                  <button
                    type="button"
                    className="wf-btn wf-btn--primary wf-btn--sm"
                    disabled={busy || !editName.trim()}
                    onClick={() => {
                      onUpdate(e.id, { name: editName.trim() });
                      setEditingId(null);
                    }}
                    style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
                  >
                    <Check size={11} strokeWidth={2} /> Salvar
                  </button>
                  <button
                    type="button"
                    className="wf-btn wf-btn--ghost wf-btn--sm"
                    disabled={busy}
                    title="Arquivar espaço (as tarefas continuam no quadro, em Todos)"
                    onClick={() => {
                      if (
                        window.confirm(
                          `Arquivar o espaço "${e.name}"? As tarefas dele continuam no quadro (em Todos) — só o espaço sai da lista.`,
                        )
                      ) {
                        onArchive(e.id);
                        setEditingId(null);
                        if (selected === e.id) onSelect(null);
                      }
                    }}
                    style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
                  >
                    <Archive size={11} strokeWidth={1.75} /> Arquivar
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {espacos.length > 0 && (
        <button
          type="button"
          onClick={() => onSelect("geral")}
          style={itemStyle(selected === "geral", dropTarget === "geral")}
          title="Tarefas sem espaço"
          {...dropProps("geral")}
        >
          <EspacoDot color={null} />
          <span style={{ flex: 1 }}>Geral</span>
          <span style={{ fontSize: 11, color: "var(--wf-ink-3)" }}>{countGeral}</span>
        </button>
      )}

      {/* Dica de soltura — só durante o arraste, pra ficar óbvio que dá pra
          mover a tarefa de espaço arrastando o card pra cá. */}
      {dragging && (
        <div
          style={{
            margin: "6px 6px 0",
            padding: "8px 10px",
            fontSize: 10.5,
            lineHeight: 1.4,
            color: "var(--nd-orange-pun)",
            background: "rgba(255,112,0,0.06)",
            border: "1px dashed var(--nd-orange)",
            borderRadius: 8,
            textAlign: "center",
          }}
        >
          Solte o card num espaço pra movê-lo
        </div>
      )}

      {creating && (
        <div
          style={{
            margin: "6px 6px 0",
            padding: 10,
            background: "var(--wf-paper)",
            border: "1px solid var(--wf-ink-5)",
            borderRadius: 8,
          }}
        >
          <input
            autoFocus
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            placeholder="Nome do espaço"
            onKeyDown={(e) => {
              if (e.key === "Enter" && draftName.trim()) {
                onCreate(draftName.trim(), draftColor);
                setCreating(false);
              }
              if (e.key === "Escape") setCreating(false);
            }}
            style={{
              width: "100%",
              border: "1px solid var(--wf-ink-5)",
              borderRadius: 6,
              padding: "5px 8px",
              fontSize: 12,
              fontFamily: "var(--wf-font-body)",
              outline: "none",
            }}
          />
          {colorPicker(draftColor, setDraftColor)}
          <button
            type="button"
            className="wf-btn wf-btn--primary wf-btn--sm"
            disabled={busy || !draftName.trim()}
            onClick={() => {
              onCreate(draftName.trim(), draftColor);
              setCreating(false);
            }}
            style={{ marginTop: 8, display: "inline-flex", alignItems: "center", gap: 4 }}
          >
            <Check size={11} strokeWidth={2} /> Criar espaço
          </button>
        </div>
      )}

      {espacos.length === 0 && !creating && (
        <div
          style={{
            margin: "4px 6px 0",
            padding: "10px 10px",
            fontSize: 11,
            lineHeight: 1.5,
            color: "var(--wf-ink-3)",
            border: "1px dashed var(--wf-ink-5)",
            borderRadius: 8,
          }}
        >
          Organize o quadro por campanha, produto ou frente — crie um espaço no “+” acima.
        </div>
      )}
    </div>
  );
}

/** Versão mobile: chips horizontais acima do quadro. */
function EspacoChips({
  espacos,
  selected,
  onSelect,
}: {
  espacos: Espaco[];
  selected: EspacoFilter;
  onSelect: (v: EspacoFilter) => void;
}) {
  if (espacos.length === 0) return null;
  const chip = (active: boolean): React.CSSProperties => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "4px 11px",
    borderRadius: 999,
    border: "1px solid",
    borderColor: active ? "var(--nd-orange)" : "var(--wf-ink-5)",
    background: active ? "rgba(255,112,0,0.08)" : "var(--wf-paper)",
    color: active ? "var(--nd-orange-pun)" : "var(--wf-ink-2)",
    fontSize: 12,
    fontWeight: 600,
    fontFamily: "var(--wf-font-body)",
    cursor: "pointer",
    whiteSpace: "nowrap",
  });
  return (
    <div
      style={{
        display: "flex",
        gap: 6,
        overflowX: "auto",
        paddingBottom: 8,
        marginBottom: 6,
      }}
    >
      <button type="button" style={chip(selected === null)} onClick={() => onSelect(null)}>
        Todos
      </button>
      {espacos.map((e) => (
        <button key={e.id} type="button" style={chip(selected === e.id)} onClick={() => onSelect(e.id)}>
          <EspacoDot color={e.color} size={7} />
          {e.name}
        </button>
      ))}
      <button type="button" style={chip(selected === "geral")} onClick={() => onSelect("geral")}>
        Geral
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Main
// ─────────────────────────────────────────────────────────────

export default function Quadro() {
  const queryClient = useQueryClient();
  const companyId = useCompanyId();
  const isMobile = useIsMobile();
  // No mobile o Kanban (rolagem horizontal) é ruim — começa em lista.
  const [view, setView] = useState<ViewMode>(isMobile ? "list" : "kanban");
  const [labelFilter, setLabelFilter] = useState<string | null>(null);
  // "Pra revisar" = só tarefas que terminaram e estão esperando você (in_review).
  const [reviewOnly, setReviewOnly] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [overTrash, setOverTrash] = useState(false);
  const draggedRef = useRef<string | null>(null);

  const issues = useQuery({
    queryKey: companyId
      ? queryKeys.companies.issues(companyId, { labelId: labelFilter ?? undefined })
      : ["companies", "none", "issues", {}],
    queryFn: () =>
      issuesApi.list(companyId!, {
        labelId: labelFilter ?? undefined,
        includeRoutineExecutions: true,
      }),
    enabled: !!companyId,
  });

  const labels = useQuery({
    queryKey: companyId ? queryKeys.companies.labels(companyId) : ["companies", "none", "labels"],
    queryFn: () => issuesApi.labels(companyId!),
    enabled: !!companyId,
  });

  // ── Espaços (projects do motor) — organização do kanban ──
  const [espacoFilter, setEspacoFilterState] = useState<EspacoFilter>(null);
  useEffect(() => {
    if (!companyId) return;
    const saved = localStorage.getItem(`quadro-espaco:${companyId}`);
    setEspacoFilterState(saved && saved !== "todos" ? saved : null);
  }, [companyId]);
  const setEspacoFilter = (v: EspacoFilter) => {
    setEspacoFilterState(v);
    if (companyId) localStorage.setItem(`quadro-espaco:${companyId}`, v ?? "todos");
  };

  const projectsQuery = useQuery({
    queryKey: ["companies", companyId, "projects"],
    queryFn: () => projectsApi.list(companyId!),
    enabled: !!companyId,
    staleTime: 30_000,
  });
  const espacos = useMemo(
    () => (projectsQuery.data ?? []).filter((p) => !p.archivedAt),
    [projectsQuery.data],
  );
  const espacoById = useMemo(() => new Map(espacos.map((e) => [e.id, e])), [espacos]);
  // Filtro salvo apontando pra espaço arquivado/apagado → volta pra "Todos".
  useEffect(() => {
    if (!projectsQuery.data) return;
    if (espacoFilter && espacoFilter !== "geral" && !espacoById.has(espacoFilter)) {
      setEspacoFilterState(null);
    }
  }, [projectsQuery.data, espacoFilter, espacoById]);

  // Nome dos agentes pro chip de responsável do card.
  const agentsQuery = useQuery({
    queryKey: ["companies", companyId, "nd-agents"],
    queryFn: () => ndAgentsApi.list(companyId!),
    enabled: !!companyId,
    staleTime: 5 * 60_000,
  });
  const agentNames = useMemo(
    () => new Map((agentsQuery.data ?? []).map((a) => [a.id, a.name])),
    [agentsQuery.data],
  );

  function invalidateEspacos() {
    queryClient.invalidateQueries({ queryKey: ["companies", companyId, "projects"] });
  }
  const createEspaco = useMutation({
    mutationFn: ({ name, color }: { name: string; color: string }) =>
      projectsApi.create(companyId!, { name, color }),
    onSuccess: (created) => {
      invalidateEspacos();
      // Já entra filtrando pelo espaço recém-criado (fluxo natural de organizar).
      if ((created as { id?: string })?.id) setEspacoFilter((created as { id: string }).id);
    },
  });
  const updateEspaco = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: { name?: string; color?: string; archivedAt?: string | null } }) =>
      projectsApi.update(id, patch),
    onSuccess: invalidateEspacos,
  });
  const moveToEspaco = useMutation({
    mutationFn: ({ issueId, projectId }: { issueId: string; projectId: string | null }) =>
      issuesApi.updateProject(issueId, projectId),
    onSuccess: invalidateIssues,
  });

  function invalidateIssues() {
    if (!companyId) return;
    queryClient.invalidateQueries({ queryKey: ["companies", companyId, "issues"] });
    queryClient.invalidateQueries({ queryKey: queryKeys.companies.dashboard(companyId) });
  }

  const updateStatus = useMutation({
    mutationFn: ({ issueId, status }: { issueId: string; status: IssueStatus }) =>
      issuesApi.updateStatus(issueId, status),
    onSuccess: invalidateIssues,
  });

  const deleteIssue = useMutation({
    mutationFn: (issueId: string) => issuesApi.delete(issueId),
    onSuccess: invalidateIssues,
  });

  function onDragStart(e: React.DragEvent, issueId: string) {
    draggedRef.current = issueId;
    setIsDragging(true);
    e.dataTransfer.effectAllowed = "move";
  }

  function onDragEnd() {
    draggedRef.current = null;
    setIsDragging(false);
    setOverTrash(false);
  }

  function onDrop(status: IssueStatus) {
    const id = draggedRef.current;
    draggedRef.current = null;
    setIsDragging(false);
    if (!id) return;
    const issue = issues.data?.find((i) => i.id === id);
    if (!issue || getStatusMeta(issue.status).primary === status) return;
    updateStatus.mutate({ issueId: id, status });
  }

  function onDropTrash() {
    const id = draggedRef.current;
    draggedRef.current = null;
    setIsDragging(false);
    setOverTrash(false);
    if (!id) return;
    deleteIssue.mutate(id);
  }

  // Soltar um card num espaço do painel = reatribuir o projectId da tarefa.
  // "geral" → sem espaço (null). "Todos" não é alvo (ambíguo).
  function onDropToEspaco(target: string) {
    const id = draggedRef.current;
    draggedRef.current = null;
    setIsDragging(false);
    if (!id) return;
    const projectId = target === "geral" ? null : target;
    const issue = issues.data?.find((i) => i.id === id);
    if (!issue || (issue.projectId ?? null) === projectId) return;
    moveToEspaco.mutate({ issueId: id, projectId });
  }

  function onClickDelete(issueId: string) {
    const issue = issues.data?.find((i) => i.id === issueId);
    const title = issue?.title ?? "essa tarefa";
    const isRunning = issue?.status === "in_progress";
    const message = isRunning
      ? `"${title}" está executando agora. Deletar mesmo assim?`
      : `Deletar "${title}"? Não dá pra desfazer.`;
    if (window.confirm(message)) {
      deleteIssue.mutate(issueId);
    }
  }

  // Cadeia de filtros: espaço primeiro, depois "pra revisar".
  const spaceFiltered = useMemo(() => {
    const all = issues.data ?? [];
    if (!espacoFilter) return all;
    if (espacoFilter === "geral") return all.filter((i) => !i.projectId);
    return all.filter((i) => i.projectId === espacoFilter);
  }, [issues.data, espacoFilter]);
  const reviewCount = useMemo(
    () => spaceFiltered.filter((i) => i.status === "in_review").length,
    [spaceFiltered],
  );
  const displayIssues = useMemo(
    () => (reviewOnly ? spaceFiltered.filter((i) => i.status === "in_review") : spaceFiltered),
    [spaceFiltered, reviewOnly],
  );
  // Contagens do painel de espaços (sempre sobre TODAS as issues carregadas).
  const espacoCounts = useMemo(() => {
    const m = new Map<string, number>();
    let geral = 0;
    for (const i of issues.data ?? []) {
      if (i.projectId) m.set(i.projectId, (m.get(i.projectId) ?? 0) + 1);
      else geral++;
    }
    return { byId: m, geral, all: (issues.data ?? []).length };
  }, [issues.data]);
  const grouped = useMemo(
    () =>
      STATUS_COLUMNS.map((col) => ({
        meta: col,
        items: displayIssues.filter((i) => columnForIssue(i) === col),
      })),
    [displayIssues],
  );

  // Rituais que rodaram nos últimos 7 dias sem criar tarefa (succeeded
  // sem linkedIssue) — vão pro footer da coluna Automação como contador.
  // Separamos em 2 grupos pra display honesto:
  //   workedCount  → fez trabalho real (ex: agentsAnalyzed > 0 em
  //                  "Aprende com aprovados", atualizou padrões)
  //   silentCount  → de fato sem ação (sem entregas pra processar)
  const routineRunsQuery = useQuery({
    queryKey: ["companies", companyId, "routine-runs"],
    queryFn: () => routinesApi.runs(companyId!, 50),
    enabled: !!companyId,
    staleTime: 60_000,
  });
  const automationCounts = useMemo(() => {
    const runs = routineRunsQuery.data ?? [];
    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    let worked = 0;
    let silent = 0;
    for (const r of runs) {
      if (r.status !== "succeeded" || r.linkedIssue) continue;
      if (new Date(r.triggeredAt).getTime() <= sevenDaysAgo) continue;
      const payload = r.triggerPayload as
        | { agentsAnalyzed?: number; costCents?: number }
        | null;
      const didWork =
        (payload?.agentsAnalyzed ?? 0) > 0 || (payload?.costCents ?? 0) > 0;
      if (didWork) worked++;
      else silent++;
    }
    return { worked, silent };
  }, [routineRunsQuery.data]);

  const totalIssues = issues.data?.length ?? 0;

  // Scroll lateral do Kanban com a roda do mouse — traduz o wheel VERTICAL em
  // scroll horizontal do board (sem precisar da barrinha inferior). MAS deixa a
  // COLUNA rolar vertical primeiro se ela ainda tem cards escondidos, pra não
  // sequestrar o scroll dos cards. Listener nativo (passive:false) pro preventDefault.
  const kanbanRef = useRef<HTMLDivElement>(null);
  // Indica que há MAIS colunas pra rolar na horizontal: degradê nas bordas que
  // some ao chegar no fim (o usuário não percebia que tinha conteúdo à direita).
  const [scrollFade, setScrollFade] = useState({ left: false, right: false });
  useEffect(() => {
    const board = kanbanRef.current;
    if (!board) return;
    const updateFade = () => {
      const max = board.scrollWidth - board.clientWidth;
      setScrollFade({ left: board.scrollLeft > 4, right: board.scrollLeft < max - 4 });
    };
    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return; // gesto horizontal → nativo
      let el = e.target as HTMLElement | null;
      while (el && el !== board) {
        const s = getComputedStyle(el);
        if ((s.overflowY === "auto" || s.overflowY === "scroll") && el.scrollHeight > el.clientHeight + 1) {
          const atTop = el.scrollTop <= 0;
          const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
          if ((e.deltaY < 0 && !atTop) || (e.deltaY > 0 && !atBottom)) return; // a coluna ainda rola
        }
        el = el.parentElement;
      }
      if (board.scrollWidth > board.clientWidth) {
        board.scrollLeft += e.deltaY;
        e.preventDefault();
      }
    };
    updateFade();
    board.addEventListener("wheel", onWheel, { passive: false });
    board.addEventListener("scroll", updateFade, { passive: true });
    window.addEventListener("resize", updateFade);
    return () => {
      board.removeEventListener("wheel", onWheel);
      board.removeEventListener("scroll", updateFade);
      window.removeEventListener("resize", updateFade);
    };
  }, [view, totalIssues, issues.isLoading]);

  if (!companyId) {
    return (
      <div style={{ padding: 32 }}>
        <span className="wf-hand-note">carregando empresa…</span>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Header — limpo, sem banner */}
      <header
        style={{
          padding: "20px 32px 14px",
          borderBottom: "1px solid var(--wf-ink-5)",
          background: "var(--wf-paper)",
          flexShrink: 0,
        }}
      >
        <div
          style={{
            maxWidth: 1400,
            margin: "0 auto",
            display: "flex",
            alignItems: "center",
            gap: 14,
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 9,
                background: "rgba(255,112,0,0.12)",
                color: "var(--nd-orange)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <LayoutGrid size={16} strokeWidth={1.75} />
            </div>
            <h1
              className="wf-display"
              style={{
                fontSize: 22,
                fontWeight: 700,
                letterSpacing: "-0.02em",
                margin: 0,
              }}
            >
              Suas tarefas
            </h1>
            <span
              style={{
                fontSize: 12,
                fontWeight: 500,
                color: "var(--wf-ink-3)",
                marginLeft: 4,
              }}
            >
              {reviewOnly
                ? `${displayIssues.length} pra revisar`
                : `${spaceFiltered.length} ${spaceFiltered.length === 1 ? "tarefa" : "tarefas"}`}
              {espacoFilter && espacoFilter !== "geral" && espacoById.get(espacoFilter)
                ? ` em ${espacoById.get(espacoFilter)!.name}`
                : espacoFilter === "geral"
                  ? " sem espaço"
                  : ""}
            </span>
          </div>

          {/* View toggle + Nova Tarefa */}
          <div style={{ marginLeft: "auto", display: "flex", gap: 10, alignItems: "center" }}>
            <button
              type="button"
              onClick={() => setReviewOnly((v) => !v)}
              title="Mostrar só as tarefas que terminaram e estão esperando você revisar"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 12px",
                borderRadius: 8,
                border: "1px solid",
                borderColor: reviewOnly ? "var(--nd-orange)" : "var(--wf-ink-5)",
                background: reviewOnly ? "var(--nd-orange)" : "var(--wf-paper)",
                color: reviewOnly ? "#fff" : "var(--wf-ink-2)",
                fontSize: 12,
                fontWeight: 600,
                fontFamily: "var(--wf-font-body)",
                cursor: "pointer",
                transition: "all 120ms",
              }}
            >
              <Eye size={13} strokeWidth={1.75} />
              Pra revisar
              {reviewCount > 0 && (
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    padding: "1px 6px",
                    borderRadius: 999,
                    background: reviewOnly ? "rgba(255,255,255,0.25)" : "var(--nd-orange)",
                    color: "#fff",
                    minWidth: 16,
                    textAlign: "center",
                  }}
                >
                  {reviewCount}
                </span>
              )}
            </button>
            <div
              style={{
                display: "flex",
                border: "1px solid var(--wf-ink-5)",
                borderRadius: 8,
                overflow: "hidden",
                background: "var(--wf-paper)",
              }}
            >
              {(
                [
                  ["kanban", LayoutGrid, "Quadro"],
                  ["list", List, "Lista"],
                ] as const
              ).map(([v, Icon, lbl]) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setView(v)}
                  style={{
                    padding: "6px 12px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                    fontSize: 12,
                    fontFamily: "var(--wf-font-body)",
                    fontWeight: 600,
                    background: view === v ? "var(--nd-blue-deep)" : "transparent",
                    color: view === v ? "#fff" : "var(--wf-ink-2)",
                    border: "none",
                    borderLeft: v === "list" ? "1px solid var(--wf-ink-5)" : "none",
                    transition: "background 120ms",
                  }}
                >
                  <Icon size={12} strokeWidth={1.75} /> {lbl}
                </button>
              ))}
            </div>
            <Link
              to="/nova-tarefa"
              className="wf-btn wf-btn--primary"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                textDecoration: "none",
              }}
            >
              <Plus size={13} strokeWidth={2} color="#fff" /> Nova Tarefa
            </Link>
          </div>
        </div>

        {/* Filter bar — labels mais aparentes */}
        {(labels.data ?? []).length > 0 && (
          <div
            style={{
              maxWidth: 1400,
              margin: "0 auto",
              display: "flex",
              alignItems: "center",
              gap: 6,
              marginTop: 12,
              flexWrap: "wrap",
            }}
          >
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: "var(--wf-ink-3)",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                marginRight: 4,
              }}
            >
              Filtrar:
            </span>
            <button
              type="button"
              onClick={() => setLabelFilter(null)}
              style={{
                padding: "4px 10px",
                fontSize: 11,
                fontWeight: 600,
                fontFamily: "var(--wf-font-body)",
                borderRadius: 999,
                border: "1px solid",
                borderColor: labelFilter === null ? "var(--wf-line)" : "var(--wf-ink-5)",
                background: labelFilter === null ? "var(--wf-line)" : "var(--wf-paper)",
                color: labelFilter === null ? "#fff" : "var(--wf-ink-2)",
                cursor: "pointer",
                transition: "all 120ms",
              }}
            >
              Todas
            </button>
            {(labels.data ?? []).map((label) => {
              const active = labelFilter === label.id;
              return (
                <button
                  key={label.id}
                  type="button"
                  onClick={() => setLabelFilter(label.id)}
                  style={{
                    padding: "4px 10px",
                    fontSize: 11,
                    fontWeight: 600,
                    fontFamily: "var(--wf-font-body)",
                    borderRadius: 999,
                    border: "1px solid",
                    borderColor: active ? "var(--nd-orange)" : "var(--wf-ink-5)",
                    background: active ? "var(--nd-orange)" : "var(--wf-paper)",
                    color: active ? "#fff" : "var(--wf-ink-2)",
                    cursor: "pointer",
                    transition: "all 120ms",
                  }}
                >
                  {label.name}
                </button>
              );
            })}
          </div>
        )}
      </header>

      {/* Body */}
      <div
        style={{
          flex: 1,
          overflow: "auto",
          padding: "20px 32px 32px",
          background: "var(--wf-bg)",
        }}
      >
        <div style={{ maxWidth: 1400, margin: "0 auto", height: "100%", display: "flex", gap: 16 }}>
          {/* Painel de Espaços (desktop) — organiza o quadro por campanha/produto */}
          {!isMobile && (
            <EspacosPanel
              espacos={espacos}
              selected={espacoFilter}
              onSelect={setEspacoFilter}
              countAll={espacoCounts.all}
              countGeral={espacoCounts.geral}
              countFor={(id) => espacoCounts.byId.get(id) ?? 0}
              onCreate={(name, color) => createEspaco.mutate({ name, color })}
              onUpdate={(id, patch) => updateEspaco.mutate({ id, patch })}
              onArchive={(id) =>
                updateEspaco.mutate({ id, patch: { archivedAt: new Date().toISOString() } })
              }
              onDropIssue={onDropToEspaco}
              dragging={isDragging}
              busy={createEspaco.isPending || updateEspaco.isPending}
            />
          )}

          <div style={{ flex: 1, minWidth: 0, height: "100%", display: "flex", flexDirection: "column" }}>
          {isMobile && (
            <EspacoChips espacos={espacos} selected={espacoFilter} onSelect={setEspacoFilter} />
          )}
          {issues.isLoading && (
            <div className="wf-hand-note" style={{ fontSize: 13 }}>
              carregando…
            </div>
          )}
          {issues.isError && (
            <div
              className="wf-card"
              style={{
                padding: 14,
                color: "var(--nd-orange-pun)",
                borderColor: "var(--nd-orange)",
              }}
            >
              erro ao carregar tarefas: {(issues.error as Error).message}
            </div>
          )}

          {!issues.isLoading && totalIssues === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="wf-card wf-card--dashed"
              style={{
                padding: 32,
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 14,
              }}
            >
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 14,
                  background: "rgba(255,112,0,0.10)",
                  color: "var(--nd-orange)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Sparkles size={20} strokeWidth={1.5} />
              </div>
              <div style={{ fontSize: 14, color: "var(--wf-ink)", fontWeight: 600 }}>
                Quadro vazio
              </div>
              <div style={{ fontSize: 13, color: "var(--wf-ink-3)", maxWidth: 320 }}>
                Crie sua primeira tarefa pra ver sua equipe trabalhando
              </div>
              <Link
                to="/nova-tarefa"
                className="wf-btn wf-btn--primary"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  textDecoration: "none",
                  marginTop: 4,
                }}
              >
                <Plus size={13} strokeWidth={2} color="#fff" /> Criar a primeira
              </Link>
            </motion.div>
          )}

          {!issues.isLoading && totalIssues > 0 && view === "kanban" && (
            <div style={{ position: "relative", flex: 1, minHeight: 0, display: "flex" }}>
              <div
                ref={kanbanRef}
                style={{
                  display: "flex",
                  gap: 14,
                  flex: 1,
                  minHeight: 0,
                  overflowX: "auto",
                  paddingBottom: 8,
                }}
              >
              {grouped.map(({ meta, items }) => (
                <KanbanColumn
                  key={meta.primary}
                  meta={meta}
                  issues={items}
                  labels={labels.data ?? []}
                  agentNames={agentNames}
                  espacoById={espacoById}
                  showEspaco={!espacoFilter || espacoFilter === "geral" ? espacos.length > 0 : false}
                  onDrop={onDrop}
                  onDragStart={onDragStart}
                  onDragEnd={onDragEnd}
                  onDeleteIssue={onClickDelete}
                  automationCounts={automationCounts}
                />
              ))}
              </div>
              {/* Degradê na ESQUERDA — aparece quando já rolou um pouco */}
              <div
                aria-hidden
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  bottom: 8,
                  width: 40,
                  pointerEvents: "none",
                  zIndex: 5,
                  background: "linear-gradient(to left, rgba(247,246,242,0), var(--wf-bg))",
                  opacity: scrollFade.left ? 1 : 0,
                  transition: "opacity .18s ease",
                }}
              />
              {/* Degradê na DIREITA + chevron — sinaliza que há mais colunas */}
              <div
                aria-hidden
                style={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  bottom: 8,
                  width: 56,
                  pointerEvents: "none",
                  zIndex: 5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  background: "linear-gradient(to right, rgba(247,246,242,0), var(--wf-bg))",
                  opacity: scrollFade.right ? 1 : 0,
                  transition: "opacity .18s ease",
                }}
              >
                <span style={{ fontSize: 30, lineHeight: 1, color: "var(--nd-orange)", marginRight: 6, fontWeight: 500 }}>›</span>
              </div>
            </div>
          )}

          {!issues.isLoading && totalIssues > 0 && view === "list" && (
            <ListView issues={displayIssues} labels={labels.data ?? []} />
          )}
          </div>
        </div>
      </div>

      {/* Trash drop zone — aparece só durante drag */}
      <AnimatePresence>
        {isDragging && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 30 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            onDragOver={(e) => {
              e.preventDefault();
              setOverTrash(true);
            }}
            onDragLeave={() => setOverTrash(false)}
            onDrop={onDropTrash}
            style={{
              position: "fixed",
              bottom: 28,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 90,
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "12px 22px",
              background: overTrash
                ? "rgba(204, 51, 68, 0.95)"
                : "rgba(20, 22, 30, 0.78)",
              color: "#fff",
              border: overTrash
                ? "1.5px dashed #fff"
                : "1px dashed rgba(255,255,255,0.4)",
              borderRadius: 999,
              backdropFilter: "blur(12px)",
              boxShadow: overTrash
                ? "0 8px 24px rgba(204,51,68,0.35)"
                : "0 6px 16px rgba(0,0,0,0.25)",
              fontSize: 13,
              fontWeight: 600,
              fontFamily: "var(--wf-font-body)",
              transition: "background 150ms, border-color 150ms",
              pointerEvents: "auto",
            }}
          >
            <Trash2 size={15} strokeWidth={2} />
            {overTrash ? "Solte pra deletar" : "Arraste aqui pra deletar"}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
